import React, { useState, useEffect } from "react";
import { FileImage } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Detection } from "@/types";
import ImageViewer from "@/components/ImageViewer";
import { cn } from "@/utils/cn";

export interface AnalysisImageViewerProps {
  // Image source - either analysisId to load from backend, or imageFile for direct upload
  analysisId?: number;
  imageFile?: File;
  detections: Detection[];
  loadImage?: (id: number) => Promise<string | null>;
  className?: string;
  topK?: number;
  onClassOverride?: (
    detectionId: number,
    newTopClass: string,
    newScore: number,
  ) => void;
  isDrawingMode?: boolean;
  onUserDrawnDetection?: (
    bbox: [number, number, number, number],
    imageElement: HTMLImageElement,
  ) => void;
  onDeleteDetection?: (detectionId: number) => void;
  autoOpenDetectionId?: number | null;
  onAutoOpenDone?: () => void;
  onPrevImage?: () => void;
  onNextImage?: () => void;
  hasPrevImage?: boolean;
  hasNextImage?: boolean;
  isAdmin?: boolean;
  imageFileName?: string;
  classValidationStatus?: Map<string, 'match' | 'mismatch' | 'unexpected'>;
}

/**
 * Wrapper component for ImageViewer that handles loading analysis images
 * and manages loading/error states with background color based on detection state
 */
export const AnalysisImageViewer: React.FC<AnalysisImageViewerProps> = ({
  analysisId,
  imageFile,
  detections,
  loadImage,
  className,
  topK = 5,
  onClassOverride,
  isDrawingMode,
  onUserDrawnDetection,
  onDeleteDetection,
  autoOpenDetectionId,
  onAutoOpenDone,
  onPrevImage,
  onNextImage,
  hasPrevImage,
  hasNextImage,
  isAdmin = false,
  imageFileName,
  classValidationStatus,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!imageFile); // Not loading if we have a file
  const [error, setError] = useState<string | null>(null);

  const hasNoDetections = detections.length === 0;
  const hasUnhandledUncertainDetections = detections.some((d) => {
    if (d.user_drawn) {
      return (
        !d.is_user_corrected &&
        (!d.is_confident ||
          d.passes_cutoff === false ||
          d.passes_refiner === false)
      );
    }
    return (
      (!d.is_confident ||
        d.passes_cutoff === false ||
        d.passes_refiner === false) &&
      !d.is_user_corrected
    );
  });
  const imageViewerBackgroundColor =
    hasNoDetections || hasUnhandledUncertainDetections
      ? "bg-orange-50"
      : "bg-green-50";

  useEffect(() => {
    // If imageFile is provided, no need to load from backend
    if (imageFile) {
      setLoading(false);
      setImageUrl(null);
      return;
    }

    // Otherwise, load from backend using analysisId
    if (!analysisId || !loadImage) {
      setLoading(false);
      setError("No image source provided");
      return;
    }

    let isMounted = true;

    const loadImageAsync = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const url = await loadImage(analysisId);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (err) {
        console.error("Failed to load analysis image:", err);
        if (isMounted) {
          setError("Failed to load image");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImageAsync();

    return () => {
      isMounted = false;
    };
  }, [analysisId, loadImage, imageFile]);

  if (loading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg min-h-96",
          className,
          imageViewerBackgroundColor,
        )}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg min-h-96",
          className,
          imageViewerBackgroundColor,
        )}
      >
        <div className="text-center text-gray-500">
          <FileImage className="h-12 w-12 mx-auto mb-2" />
          <p>{error || "Failed to load image"}</p>
        </div>
      </div>
    );
  }

  return (
    <ImageViewer
      imageFile={imageFile}
      imageUrl={imageUrl || undefined}
      detections={detections}
      className={cn(className, imageViewerBackgroundColor)}
      topK={topK}
      onClassOverride={onClassOverride}
      isDrawingMode={isDrawingMode}
      onUserDrawnDetection={onUserDrawnDetection}
      onDeleteDetection={onDeleteDetection}
      autoOpenDetectionId={autoOpenDetectionId}
      onAutoOpenDone={onAutoOpenDone}
      onPrevImage={onPrevImage}
      onNextImage={onNextImage}
      hasPrevImage={hasPrevImage}
      hasNextImage={hasNextImage}
      isAdmin={isAdmin}
      imageFileName={imageFileName}
      classValidationStatus={classValidationStatus}
    />
  );
};
